/**
 * =====================================================================================
 * MÓDULO VUEX DE DOCUMENTOS - documentos.js
 * =====================================================================================
 *
 * Este módulo gestiona los DOCUMENTOS TRIBUTARIOS ELECTRÓNICOS (DTE) emitidos.
 * Un DTE es una factura electrónica válida ante el Ministerio de Hacienda.
 *
 * ¿QUÉ ES UN DTE?
 * ---------------
 * DTE = Documento Tributario Electrónico
 * Es la versión digital de las facturas, validada por el Ministerio de Hacienda.
 *
 * TIPOS DE DTE EN EL SALVADOR:
 * - 01: Factura de Consumidor Final
 * - 03: Comprobante de Crédito Fiscal (CCF)
 * - 05: Nota de Crédito
 * - 06: Nota de Débito
 * - 07: Nota de Remisión
 * - 11: Factura de Exportación
 * - 14: Factura de Sujeto Excluido
 *
 * ESTADOS DE UN DTE:
 * -----------------
 * - PROCESANDO: Se está enviando al MH
 * - TRANSMITIDO: El MH lo aceptó correctamente (estado exitoso)
 * - RECHAZADO: El MH lo rechazó por errores
 * - CONTINGENCIA: No se pudo enviar, guardado localmente
 * - INVALIDADO: Fue anulado después de ser transmitido
 *
 * ESTRUCTURA DE UN DOCUMENTO:
 * --------------------------
 * {
 *   id: 1,
 *   tipoDte: "01",                    // Tipo de documento
 *   codigoGeneracion: "UUID...",      // UUID único generado
 *   numeroControl: "DTE-01-M001P001-000000000000001",
 *   selloRecepcion: "...",            // Sello del MH al aceptar
 *   fechaEmision: "2024-01-15",
 *   horaEmision: "10:30:00",
 *   receptor: { ... },
 *   cuerpoDocumento: [ ... ],         // Líneas/items de la factura
 *   resumen: { ... },                 // Totales
 *   estado: "TRANSMITIDO"
 * }
 *
 * NOTA IMPORTANTE - DATOS MOCK:
 * ----------------------------
 * Este módulo actualmente usa DATOS MOCK (falsos) para desarrollo.
 * Los datos vienen de src/mock/documentos.js
 * TODO: Conectar con el backend real cuando esté listo.
 *
 * RELACIÓN CON OTROS ARCHIVOS:
 * ----------------------------
 * - src/mock/documentos.js: Datos de prueba
 * - src/services/facturas.service.js: Servicio real para crear facturas
 * - src/views/consultas/ConsultaDocumentos.vue: Lista de documentos
 * - src/views/emision/FacturaView.vue: Crear nueva factura
 *
 * =====================================================================================
 */

// Servicio de facturas para conectar con el backend
import facturasService from '@/services/facturas.service'

/**
 * =====================================================================================
 * STATE (Estado del Módulo)
 * =====================================================================================
 *
 * Contiene la lista de documentos y el documento en edición.
 */
const state = {
  /**
   * Lista de todos los documentos emitidos
   * Se carga desde mock o backend
   */
  documentos: [],

  /**
   * Documento actualmente en edición/emisión
   * Se usa mientras se está creando una nueva factura
   * null cuando no hay documento en proceso
   */
  documentoActual: null,

  /**
   * Flag de carga
   */
  loading: false,

  /**
   * Metadata de paginación del servidor
   */
  paginacion: {
    totalCount: 0,
    currentPage: 1,
    pageSize: 10,
    totalPages: 0
  },

  /**
   * Filtros actuales aplicados a la lista de documentos
   * Permiten buscar y filtrar documentos en la UI
   */
  filtros: {
    /**
     * Filtro por rango de fechas
     * Formato: "YYYY-MM-DD"
     */
    fechaDesde: null,
    fechaHasta: null,

    /**
     * Filtro por tipo de DTE
     * Valores: "01", "03", "05", etc.
     */
    tipoDte: null,

    /**
     * Filtro por estado
     * Valores: "TRANSMITIDO", "RECHAZADO", "CONTINGENCIA", "INVALIDADO"
     */
    estado: null,

    /**
     * Término de búsqueda libre
     * Busca en: nombre receptor, número de control, documento receptor
     */
    busqueda: ''
  }
}

/**
 * =====================================================================================
 * GETTERS (Propiedades Computadas)
 * =====================================================================================
 *
 * Proporcionan acceso a los documentos y datos derivados.
 */
const getters = {
  /**
   * Retorna todos los documentos sin filtrar
   */
  allDocumentos: state => state.documentos,

  /**
   * Retorna el documento en edición actual
   */
  documentoActual: state => state.documentoActual,

  /**
   * Estado de carga
   */
  loading: state => state.loading,

  /**
   * Metadata de paginación del servidor
   */
  paginacion: state => state.paginacion,

  /**
   * ===========================================================================
   * GETTER: documentosFiltrados
   * ===========================================================================
   *
   * Retorna los documentos filtrados según los filtros actuales.
   * Aplica todos los filtros activos de forma acumulativa.
   *
   * LÓGICA DE FILTRADO:
   * ------------------
   * Cada filtro es opcional. Solo se aplica si tiene valor.
   * Los filtros se aplican uno tras otro (AND lógico):
   * - Si busqueda está activa Y tipoDte está activo, el documento
   *   debe cumplir AMBOS criterios para aparecer.
   *
   * MÉTODOS DE ARRAY UTILIZADOS:
   * ---------------------------
   * - filter(): Crea nuevo array con elementos que pasan el test
   * - includes(): Verifica si string contiene substring
   * - toLowerCase(): Convierte a minúsculas para búsqueda insensible
   *
   * @returns {Array} Documentos que cumplen todos los filtros
   */
  documentosFiltrados: state => {
    // PENDIENTE_LOTE y PENDIENTE_ENVIO se excluyen desde el backend (server-side)
    // Solo se incluyen si el usuario busca un DTE específico (search)
    let docs = [...state.documentos]
    const totalInicial = docs.length

    // =======================================================================
    // FILTRO: Búsqueda de texto libre
    // =======================================================================
    if (state.filtros.busqueda) {
      // Convertir a minúsculas para búsqueda insensible a mayúsculas
      const busqueda = state.filtros.busqueda.toLowerCase()

      docs = docs.filter(d =>
        // Buscar en nombre del receptor
        d.receptor?.nombre?.toLowerCase().includes(busqueda) ||
        // Buscar en número de control
        d.numeroControl?.toLowerCase().includes(busqueda) ||
        // Buscar en documento del receptor (NIT)
        d.receptor?.documento?.toLowerCase().includes(busqueda) ||
        // Buscar en código de generación (UUID)
        d.codigoGeneracion?.toLowerCase().includes(busqueda)
      )
    }

    // =======================================================================
    // FILTRO: Tipo de DTE
    // =======================================================================
    if (state.filtros.tipoDte) {
      docs = docs.filter(d => d.tipoDte === state.filtros.tipoDte)
    }

    // =======================================================================
    // FILTRO: Estado del documento
    // =======================================================================
    if (state.filtros.estado) {
      docs = docs.filter(d => d.estado === state.filtros.estado)
    }

    // =======================================================================
    // FILTRO: Fecha desde (mayor o igual)
    // Extraer solo la parte de fecha (YYYY-MM-DD) para comparación robusta
    // =======================================================================
    const docsAntesFiltroDeFecha = docs.length
    if (state.filtros.fechaDesde) {
      const fechaDesdeStr = state.filtros.fechaDesde.substring(0, 10)
      docs = docs.filter(d => {
        const fechaDoc = d.fechaEmision ? d.fechaEmision.substring(0, 10) : ''
        return fechaDoc >= fechaDesdeStr
      })
    }

    // =======================================================================
    // FILTRO: Fecha hasta (menor o igual)
    // Extraer solo la parte de fecha (YYYY-MM-DD) para comparación robusta
    // =======================================================================
    if (state.filtros.fechaHasta) {
      const fechaHastaStr = state.filtros.fechaHasta.substring(0, 10)
      docs = docs.filter(d => {
        const fechaDoc = d.fechaEmision ? d.fechaEmision.substring(0, 10) : ''
        return fechaDoc <= fechaHastaStr
      })
    }

    return docs
  },

  /**
   * ===========================================================================
   * GETTER: estadisticas
   * ===========================================================================
   *
   * Calcula estadísticas rápidas sobre los documentos.
   * Se muestran en el dashboard o panel principal.
   *
   * CÁLCULOS:
   * - totalHoy: Cantidad de documentos emitidos hoy
   * - montoHoy: Suma de montos de documentos de hoy
   * - transmitidos: Total de documentos con estado TRANSMITIDO
   * - pendientes: Documentos en CONTINGENCIA o PROCESANDO
   *
   * MÉTODOS UTILIZADOS:
   * ------------------
   * - filter(): Filtrar documentos por condición
   * - reduce(): Acumular valores (suma de montos)
   *
   * @returns {Object} { totalHoy, montoHoy, transmitidos, pendientes }
   */
  estadisticas: state => {
    // Obtener fecha de hoy en formato YYYY-MM-DD
    const _now = new Date(), _pad = (n) => String(n).padStart(2, '0')
    const hoy = `${_now.getFullYear()}-${_pad(_now.getMonth() + 1)}-${_pad(_now.getDate())}`

    // Filtrar documentos emitidos hoy
    const docsHoy = state.documentos.filter(d => d.fechaEmision === hoy)

    return {
      // Cantidad de documentos de hoy
      totalHoy: docsHoy.length,

      // Suma de montos de hoy
      // reduce((acumulador, elemento) => ..., valorInicial)
      // En cada iteración: sum = sum + d.montoTotal
      montoHoy: docsHoy.reduce((sum, d) => sum + d.montoTotal, 0),

      // Cantidad de documentos procesados/aprobados exitosamente
      transmitidos: state.documentos.filter(d => ['PROCESADO', 'APROBADO'].includes(d.estado)).length,

      // Cantidad de documentos pendientes o en proceso
      pendientes: state.documentos.filter(d =>
        d.estado === 'PENDIENTE' || d.estado === 'GENERADO'
      ).length
    }
  },

  /**
   * ===========================================================================
   * GETTER: documentosRecientes
   * ===========================================================================
   *
   * Retorna los últimos 5 documentos ordenados por fecha/hora.
   * Se usa para mostrar actividad reciente en el dashboard.
   *
   * ORDENAMIENTO:
   * ------------
   * sort() ordena el array comparando elementos de a pares.
   * sort((a, b) => ...) - función de comparación:
   * - Retorna negativo si a va antes que b
   * - Retorna positivo si a va después que b
   * - Retorna 0 si son iguales
   *
   * Para orden descendente (más reciente primero):
   * fechaB - fechaA (el mayor va primero)
   *
   * @returns {Array} Los 5 documentos más recientes
   */
  documentosRecientes: state => {
    // Crear copia del array antes de ordenar (sort modifica el original)
    return [...state.documentos]
      // Ordenar por fecha+hora descendente
      .sort((a, b) => {
        // Crear objetos Date a partir de fecha y hora
        // new Date("2024-01-15 10:30:00") crea un Date válido
        const fechaA = new Date(a.fechaEmision + ' ' + a.horaEmision)
        const fechaB = new Date(b.fechaEmision + ' ' + b.horaEmision)
        // Resta de Dates da diferencia en milisegundos
        // Si fechaB > fechaA, resultado es positivo → B va primero
        return fechaB - fechaA
      })
      // Tomar solo los primeros 5
      .slice(0, 5)
  }
}

/**
 * =====================================================================================
 * MUTATIONS (Modificadores del Estado)
 * =====================================================================================
 *
 * Funciones síncronas que modifican el state directamente.
 */
const mutations = {
  /**
   * Establece la lista de documentos
   * @param {Object} state - Estado del módulo
   * @param {Array} documentos - Lista de documentos
   */
  SET_DOCUMENTOS(state, documentos) {
    state.documentos = documentos
  },

  /**
   * Establece el documento en edición
   * @param {Object} state - Estado del módulo
   * @param {Object|null} documento - Documento o null para limpiar
   */
  SET_DOCUMENTO_ACTUAL(state, documento) {
    state.documentoActual = documento
  },

  /**
   * Establece el estado de carga
   */
  SET_LOADING(state, loading) {
    state.loading = loading
  },

  /**
   * Actualiza los filtros
   * Usa spread para merge: mantiene filtros existentes y actualiza los nuevos
   *
   * EJEMPLO:
   * filtros = { busqueda: '', tipoDte: null }
   * SET_FILTROS(state, { tipoDte: '01' })
   * → filtros = { busqueda: '', tipoDte: '01' }
   *
   * @param {Object} state - Estado del módulo
   * @param {Object} filtros - Filtros a actualizar (parcial)
   */
  SET_FILTROS(state, filtros) {
    state.filtros = { ...state.filtros, ...filtros }
  },

  SET_PAGINACION(state, paginacion) {
    state.paginacion = { ...state.paginacion, ...paginacion }
  },

  /**
   * Resetea todos los filtros a valores iniciales
   */
  CLEAR_FILTROS(state) {
    state.filtros = {
      fechaDesde: null,
      fechaHasta: null,
      tipoDte: null,
      estado: null,
      busqueda: ''
    }
  },

  /**
   * Agrega un nuevo documento al inicio de la lista
   * unshift() inserta al principio (los más recientes primero)
   *
   * @param {Object} state - Estado del módulo
   * @param {Object} documento - Documento a agregar
   */
  ADD_DOCUMENTO(state, documento) {
    state.documentos.unshift(documento)
  },

  /**
   * Actualiza un documento existente
   * Busca por ID y hace merge de los datos
   *
   * @param {Object} state - Estado del módulo
   * @param {Object} payload - { id, data } - ID del documento y datos a actualizar
   */
  UPDATE_DOCUMENTO(state, { id, data }) {
    // findIndex retorna el índice donde se cumple la condición, o -1 si no lo encuentra
    const index = state.documentos.findIndex(d => d.id === id)
    if (index !== -1) {
      // Merge de datos existentes con los nuevos
      state.documentos[index] = { ...state.documentos[index], ...data }
    }
  }
}

/**
 * =====================================================================================
 * ACTIONS (Acciones Asíncronas)
 * =====================================================================================
 *
 * Funciones que pueden ser asíncronas y coordinan las operaciones.
 *
 * NOTA: La mayoría de estas acciones usan datos MOCK.
 * Simula delays con setTimeout para imitar comportamiento de red.
 */
const actions = {
  /**
   * ===========================================================================
   * ACTION: fetchDocumentos
   * ===========================================================================
   *
   * Carga la lista de documentos.
   * ACTUALMENTE USA MOCK - TODO: Conectar con backend.
   *
   * SIMULACIÓN DE DELAY:
   * -------------------
   * new Promise(resolve => setTimeout(resolve, 500))
   * Crea una promesa que se resuelve después de 500ms.
   * Simula el tiempo de respuesta de una API real.
   *
   * @param {Object} context - { commit, rootState }
   */
  async fetchDocumentos({ commit, rootState }, options = {}) {
    commit('SET_LOADING', true)
    try {
      // Determinar sucursalId:
      // - Si se pasa explícitamente (incluso null), usarlo
      // - Si no se pasa nada, usar el del store de emisor como fallback
      const sucursalIdFinal = 'sucursalId' in options
        ? options.sucursalId
        : (rootState.emisor?.establecimientoActual?.id ?? null)

      // Obtener facturas desde el backend con filtros y paginación
      const params = {
        pageNumber: options.pageNumber || 1,
        pageSize: options.pageSize || 10
      }
      if (sucursalIdFinal) {
        params.sucursalId = sucursalIdFinal
      }
      // Agregar filtros de fecha si están presentes
      // NOTA: El backend no parsea bien fechas con hora (T23:59:59 causa Error 500)
      // Por ahora enviamos solo la fecha. El backend debería comparar solo la parte de fecha.
      if (options.fechaDesde) {
        // Extraer solo YYYY-MM-DD si viene con hora
        params.fechaDesde = typeof options.fechaDesde === 'string'
          ? options.fechaDesde.substring(0, 10)
          : options.fechaDesde
      }
      if (options.fechaHasta) {
        // Extraer solo YYYY-MM-DD si viene con hora
        params.fechaHasta = typeof options.fechaHasta === 'string'
          ? options.fechaHasta.substring(0, 10)
          : options.fechaHasta
      }
      // Agregar búsqueda si está presente
      if (options.search) {
        params.search = options.search
      }
      // Agregar filtro por vendedor si está presente
      if (options.vendedorId) {
        params.vendedorId = options.vendedorId
      }
      // Agregar filtro por tipo DTE y estado (server-side)
      if (options.tipoDte) {
        params.tipoDte = options.tipoDte
      }
      if (options.estadoHacienda) {
        params.estadoHacienda = options.estadoHacienda
      }
      // Filtrar por ambiente (Pruebas/Producción)
      if (options.ambiente) {
        params.ambiente = options.ambiente
      }
      const response = await facturasService.getAll(params)

      // Guardar metadata de paginación del servidor
      commit('SET_PAGINACION', {
        totalCount: response.totalCount || 0,
        currentPage: response.currentPage || params.pageNumber,
        pageSize: response.pageSize || params.pageSize,
        totalPages: response.totalPages || 0
      })

      // Función para extraer el código de tipo DTE del número de control
      // Formato: DTE-XX-XXXXXXXX-XXXXXXXXXXXXXXX donde XX es el tipo
      const extraerTipoDte = (numeroControl, tipoDocumento) => {
        // Primero intentar extraer del número de control
        if (numeroControl) {
          const match = numeroControl.match(/^DTE-(\d{2})-/)
          if (match) return match[1]
        }
        // Si no, mapear desde el nombre descriptivo
        const mapaTipos = {
          'factura': '01',
          'consumidor final': '01',
          'ccf': '03',
          'crédito fiscal': '03',
          'credito fiscal': '03',
          'comprobante de crédito fiscal': '03',
          'nota de crédito': '05',
          'nota de débito': '06',
          'comprobante de retención': '07',
          'factura de exportación': '11',
          'factura sujeto excluido': '14'
        }
        const tipoNormalizado = (tipoDocumento || '').toLowerCase().trim()
        return mapaTipos[tipoNormalizado] || '01'
      }

      // Función para obtener descripción del tipo DTE
      const obtenerDescTipoDte = (codigo) => {
        const descripciones = {
          '01': 'Factura',
          '03': 'Crédito Fiscal',
          '05': 'Nota de Crédito',
          '06': 'Nota de Débito',
          '07': 'Comprobante de Retención',
          '11': 'Factura de Exportación',
          '14': 'Factura Sujeto Excluido'
        }
        return descripciones[codigo] || 'Documento'
      }

      // Mapear respuesta del backend al formato esperado por el frontend
      const docs = (response.items || []).map(factura => {
        const tipoDte = extraerTipoDte(factura.numeroControl, factura.tipoDocumento)
        return {
          id: factura.id,
          tipoDte,
          tipoDteDesc: obtenerDescTipoDte(tipoDte),
          codigoGeneracion: factura.codigoGeneracion,
          numeroControl: factura.numeroControl,
          fechaEmision: factura.fechaEmision?.split('T')[0] || factura.fechaEmision,
          horaEmision: factura.horaEmision
            || (factura.fechaEmision?.includes('T') ? factura.fechaEmision.split('T')[1]?.split('.')[0] : null)
            || '00:00:00',
          receptor: {
            nombre: factura.receptorNombre,
            documento: factura.receptorNumeroDocumento,
            telefono: factura.receptorTelefono,
            correo: factura.receptorCorreo
          },
          montoTotal: factura.totalPagar,
          estado: (factura.estadoHacienda || 'GENERADO').toUpperCase(),
          estadoDesc: factura.estadoHacienda || 'Generado',
          ambiente: factura.ambiente === '00' ? 'PRUEBAS' : factura.ambiente === '01' ? 'PRODUCCION' : (factura.ambiente || 'PRUEBAS'),
          selloRecepcion: factura.selloRecepcion,
          fechaTransmision: factura.fechaTransmision?.split('T')[0] || factura.fechaTransmision || null,
          horaTransmision: factura.horaTransmision || null,
          activo: factura.activo,
          vendedorId: factura.vendedorId || null,
          vendedorNombre: factura.vendedorNombre || null,
          vendedorCodigo: factura.vendedorCodigo || null,
          cajaCodigo: factura.cajaCodigo || null,
          usuarioId: factura.usuarioId || null
        }
      })

      commit('SET_DOCUMENTOS', docs)
    } catch (error) {
      console.error('Error al cargar documentos:', error)
      commit('SET_DOCUMENTOS', [])
    } finally {
      commit('SET_LOADING', false)
    }
  },

  /**
   * ===========================================================================
   * ACTION: setFiltros
   * ===========================================================================
   *
   * Aplica filtros a la lista de documentos.
   * Los getters se recalculan automáticamente cuando cambian los filtros.
   *
   * @param {Object} context - { commit }
   * @param {Object} filtros - Filtros a aplicar
   */
  setFiltros({ commit }, filtros) {
    commit('SET_FILTROS', filtros)
  },

  /**
   * ===========================================================================
   * ACTION: clearFiltros
   * ===========================================================================
   *
   * Limpia todos los filtros aplicados.
   * Muestra todos los documentos sin filtrar.
   *
   * @param {Object} context - { commit }
   */
  clearFiltros({ commit }) {
    commit('CLEAR_FILTROS')
  },

  /**
   * ===========================================================================
   * ACTION: iniciarDocumento
   * ===========================================================================
   *
   * Inicia un nuevo documento para emisión.
   * Crea la estructura básica del documento según el tipo de DTE.
   *
   * ESTRUCTURA INICIAL:
   * ------------------
   * Crea un documento "vacío" con valores por defecto.
   * El usuario irá llenando los datos en el formulario.
   *
   * @param {Object} context - { commit }
   * @param {string} tipoDte - Tipo de documento ("01", "03", etc.)
   */
  iniciarDocumento({ commit }, tipoDte) {
    // Crear estructura básica del nuevo documento
    const nuevoDoc = {
      // Tipo de DTE seleccionado
      tipoDte,

      // Fecha y hora actuales (usar métodos locales, no UTC)
      fechaEmision: (() => {
        const d = new Date()
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
      })(),
      horaEmision: (() => {
        const d = new Date()
        return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}:${String(d.getSeconds()).padStart(2, '0')}`
      })(),

      // Datos del receptor (cliente) - se llenará en el formulario
      receptor: null,

      // Líneas/items del documento - se agregarán desde el formulario
      cuerpoDocumento: [],

      // Resumen de totales - se calculará automáticamente
      resumen: {
        totalGravada: 0,      // Total con IVA
        totalExenta: 0,       // Total exento de IVA
        totalNoSujeta: 0,     // Total no sujeto a IVA
        subTotal: 0,          // Subtotal antes de IVA
        descuento: 0,         // Total de descuentos
        iva: 0,               // IVA calculado
        totalPagar: 0         // Total final
      },

      // Condición de operación (1=Contado por defecto)
      condicionOperacion: 1,

      // Formas de pago - se agregarán desde el formulario
      pagos: [],

      // Extensión (entrega/recepción) - opcional
      extension: null
    }

    commit('SET_DOCUMENTO_ACTUAL', nuevoDoc)
  },

  /**
   * ===========================================================================
   * ACTION: emitirDocumento (MOCK)
   * ===========================================================================
   *
   * Emite/guarda el documento actual.
   * ACTUALMENTE USA MOCK - TODO: Conectar con backend.
   *
   * PROCESO REAL (cuando se conecte al backend):
   * 1. Validar documento
   * 2. Generar UUID y número de control
   * 3. Firmar documento
   * 4. Enviar al MH
   * 5. Guardar respuesta
   *
   * PROCESO MOCK:
   * 1. Simular delay
   * 2. Generar códigos fake
   * 3. Marcar como TRANSMITIDO
   * 4. Agregar a la lista
   *
   * @param {Object} context - { commit, state }
   * @returns {Object} { success: boolean, documento?: Object, error?: string }
   */
  async emitirDocumento({ commit, state }) {
    commit('SET_LOADING', true)
    try {
      // Simular tiempo de procesamiento
      await new Promise(resolve => setTimeout(resolve, 1500))

      // Generar ID mock (normalmente viene del backend)
      // Math.max(...array.map(d => d.id)) obtiene el ID más alto
      const nuevoId = Math.max(...documentosEmitidos.map(d => d.id)) + 1

      // Generar UUID mock
      // crypto.randomUUID() genera UUID v4
      const uuid = crypto.randomUUID().toUpperCase()

      // Crear documento emitido con códigos generados
      const documentoEmitido = {
        // Spread: copiar todos los datos del documento actual
        ...state.documentoActual,

        // Agregar campos generados
        id: nuevoId,
        codigoGeneracion: uuid,

        // Generar número de control: DTE-{tipo}-{establecimiento}-{correlativo}
        // padStart(15, '0') rellena con ceros hasta 15 caracteres
        // String(5).padStart(15, '0') → "000000000000005"
        numeroControl: `DTE-${state.documentoActual.tipoDte}-M001P001-${String(nuevoId).padStart(15, '0')}`,

        // Generar sello mock (normalmente viene del MH)
        // replace(/-/g, '') quita todos los guiones
        // substring(0, 32) toma los primeros 32 caracteres
        selloRecepcion: crypto.randomUUID().replace(/-/g, '').toUpperCase().substring(0, 32),

        // Marcar como procesado exitosamente (según backend)
        estado: 'PROCESADO',
        estadoDesc: 'Procesado'
      }

      // Agregar a la lista de documentos
      commit('ADD_DOCUMENTO', documentoEmitido)

      // Actualizar el documento actual con los códigos generados
      commit('SET_DOCUMENTO_ACTUAL', documentoEmitido)

      return { success: true, documento: documentoEmitido }
    } catch (error) {
      return { success: false, error: error.message }
    } finally {
      commit('SET_LOADING', false)
    }
  },

  /**
   * ===========================================================================
   * ACTION: invalidarDocumento (MOCK)
   * ===========================================================================
   *
   * Invalida (anula) un documento ya transmitido.
   * ACTUALMENTE USA MOCK - TODO: Conectar con backend.
   *
   * PROCESO DE INVALIDACIÓN:
   * -----------------------
   * 1. Validar que el documento pueda invalidarse
   * 2. Registrar motivo y responsable
   * 3. Enviar solicitud de invalidación al MH
   * 4. Actualizar estado local
   *
   * REGLAS DE INVALIDACIÓN:
   * ----------------------
   * - Solo documentos TRANSMITIDOS pueden invalidarse
   * - Hay un plazo máximo para invalidar (según normativa MH)
   * - Requiere motivo y datos del responsable
   *
   * @param {Object} context - { commit }
   * @param {Object} payload - { id, motivo, responsable }
   * @returns {Object} { success: boolean, error?: string }
   */
  async invalidarDocumento({ commit }, { id, motivo, responsable }) {
    commit('SET_LOADING', true)
    try {
      // Simular tiempo de procesamiento
      await new Promise(resolve => setTimeout(resolve, 1000))

      // Actualizar el documento con datos de invalidación
      commit('UPDATE_DOCUMENTO', {
        id,
        data: {
          // Cambiar estado (ANULADO según backend)
          estado: 'ANULADO',
          estadoDesc: 'Anulado',

          // Registrar datos de invalidación
          motivoInvalidacion: motivo,
          responsableInvalidacion: responsable,
          fechaInvalidacion: new Date().toISOString()
        }
      })

      return { success: true }
    } catch (error) {
      return { success: false, error: error.message }
    } finally {
      commit('SET_LOADING', false)
    }
  }
}

/**
 * =====================================================================================
 * EXPORTACIÓN DEL MÓDULO
 * =====================================================================================
 *
 * namespaced: true significa que las llamadas incluyen 'documentos/':
 * - store.getters['documentos/allDocumentos']
 * - store.dispatch('documentos/fetchDocumentos')
 *
 * FLUJO TÍPICO DE USO:
 * -------------------
 *
 * 1. Cargar documentos al entrar a la vista de consulta:
 *    await store.dispatch('documentos/fetchDocumentos')
 *
 * 2. Mostrar documentos filtrados:
 *    const docs = computed(() => store.getters['documentos/documentosFiltrados'])
 *
 * 3. Aplicar filtros desde la UI:
 *    store.dispatch('documentos/setFiltros', { tipoDte: '01' })
 *
 * 4. Mostrar estadísticas en dashboard:
 *    const stats = computed(() => store.getters['documentos/estadisticas'])
 *
 * 5. Crear nueva factura:
 *    store.dispatch('documentos/iniciarDocumento', '01')
 *    // Usuario llena el formulario...
 *    const result = await store.dispatch('documentos/emitirDocumento')
 *
 * 6. Invalidar documento:
 *    await store.dispatch('documentos/invalidarDocumento', {
 *      id: 1,
 *      motivo: 'Error en datos',
 *      responsable: { nombre: '...', documento: '...' }
 *    })
 */
export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
